import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ExternalLink } from 'lucide-react';
import type { ConfirmActionResponse } from '../../types/agent.types';

interface ConfirmationCardProps {
  preview: string;
  onConfirm: () => Promise<void>;
  onCancel: () => Promise<void>;
  confirmResult: ConfirmActionResponse | null;
}

const ConfirmationCard: React.FC<ConfirmationCardProps> = ({
  preview,
  onConfirm,
  onCancel,
  confirmResult,
}) => {
  const navigate = useNavigate();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await onCancel();
    } finally {
      setIsCancelling(false);
    }
  };

  // Success state
  if (confirmResult?.success) {
    const rawId = confirmResult.result?.id;
    const resultId = rawId != null && /^\d+$/.test(String(rawId)) ? String(rawId) : undefined;
    const hasLink = resultId && confirmResult.tool_name?.includes('b2b');

    return (
      <div style={{
        margin: '0 var(--space-4)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--success-50)',
        border: '1px solid var(--success-200)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <div className="flex items-center gap-2 mb-2">
          <Check size={16} className="text-success-600" />
          <span className="text-sm font-semibold text-success-700">
            {confirmResult.message}
          </span>
        </div>

        {hasLink && (
          <button
            type="button"
            onClick={() => navigate(`/admin/b2b/${resultId}`)}
            className="flex items-center gap-1 bg-surface-1 rounded-md cursor-pointer text-xs text-success-700 font-medium" style={{ padding: 'var(--space-1) var(--space-3)', border: '1px solid var(--success-300)', fontFamily: 'inherit' }}
          >
            <ExternalLink size={12} />
            Открыть в B2B
          </button>
        )}
      </div>
    );
  }

  // Pending state
  return (
    <div className="bg-info-50 rounded-lg" style={{ margin: '0 var(--space-4)', padding: 'var(--space-3) var(--space-4)', border: '1px solid var(--info-200)' }}>
      <span className="d-block text-xs font-semibold text-info-700 mb-2 tracking-wide" style={{ textTransform: 'uppercase' as const }}>
        Подтверждение
      </span>

      <pre className="p-2 bg-surface-1 rounded-md text-xs text-gray-800 font-mono overflow-auto" style={{ margin: '0 0 var(--space-3)', border: '1px solid var(--info-100)', whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const, lineHeight: 'var(--leading-relaxed)', maxHeight: 200 }}>
        {preview}
      </pre>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirming || isCancelling}
          className="btn-primary text-xs flex items-center gap-1" style={{ padding: 'var(--space-1) var(--space-3)', minHeight: 32 }}
        >
          <Check size={14} />
          {isConfirming ? 'Подтверждение...' : 'Подтвердить'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={isConfirming || isCancelling}
          className="btn-secondary"
          style={{
            fontSize: 'var(--text-xs)',
            padding: 'var(--space-1) var(--space-3)',
            minHeight: 32,
          }}
        >
          {isCancelling ? 'Отмена...' : 'Отменить'}
        </button>
      </div>
    </div>
  );
};

export default ConfirmationCard;
